import { numberWithCommas, capitalizeFirstLetter } from "../utils";
import { StandaloneCandidate } from "./StandaloneDatabaseService";
import { waBroadCastMessageTemplates } from "../standaloneWaBroadCastTemplate";

/**
 * Converts a name to title case (first letter of each word capitalized)
 * Example: "aMIT Kii" -> "Amit Kii"
 */
export const toTitleCase = (name?: string): string => {
  if (!name) return "";

  return name
    .toLowerCase()
    .split(" ")
    .map(word => {
      if (word.length === 0) return word;
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(" ");
};

export const formatSalary = (salary?: number): string => {
  if (!salary) return "-";
  return `₹${numberWithCommas(salary)} / month`;
};

export const formatDate = (dateString?: string): string => {
  if (!dateString) return "-";
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  } catch {
    return dateString;
  }
};

export const getGenderDisplay = (gender?: string): string => {
  if (!gender) return "-";
  return capitalizeFirstLetter(gender);
};

export const getProfilePicture = (candidate: StandaloneCandidate): string => {
  if (candidate.profile_picture) {
    return candidate.profile_picture;
  }
  if (candidate.gender === "Male") {
    return "/hire/database/man.png";
  }
  return "/hire/database/woman.png";
};

export const getTimeAgo = (timestamp?: string, prefix?: string): string => {
  if (!timestamp) return "-";

  try {
    const now = new Date();
    const past = new Date(timestamp);
    const diffInSeconds = Math.floor((now.getTime() - past.getTime()) / 1000);

    if (diffInSeconds < 60) {
      const unit = diffInSeconds === 1 ? "second" : "seconds";
      return `${prefix ? prefix + " " : ""}${diffInSeconds} ${unit} ago`;
    }

    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
      const unit = diffInMinutes === 1 ? "minute" : "minutes";
      return `${prefix ? prefix + " " : ""}${diffInMinutes} ${unit} ago`;
    }

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      const unit = diffInHours === 1 ? "hour" : "hours";
      return `${prefix ? prefix + " " : ""}${diffInHours} ${unit} ago`;
    }

    const diffInDays = Math.floor(diffInHours / 24);
    const unit = diffInDays === 1 ? "day" : "days";
    return `${prefix ? prefix + " " : ""}${diffInDays} ${unit} ago`;
  } catch {
    return timestamp;
  }
};

export const formatAudioDuration = (milliseconds?: number): string => {
  if (!milliseconds || milliseconds < 0) return "00:00";

  const totalSeconds = Math.floor(milliseconds / 1000);
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;

  const formattedMinutes = String(mins).padStart(2, "0");
  const formattedSeconds = String(secs).padStart(2, "0");

  return `${formattedMinutes}:${formattedSeconds}`;
};

export const RESET_ALL_MODAL_TYPE = "standaloneDbResetAll";
/**
 * Replace template placeholders with actual values
 * @param template - Template string with {{placeholder}} syntax
 * @param data - Object containing replacement values
 * @returns String with placeholders replaced
 */
const replacePlaceholders = (
  template: string,
  data: Record<string, any>
): string => {
  return template.replace(/{{(.*?)}}/g, (_, key) => {
    const trimmedKey = key.trim();
    return trimmedKey in data ? data[trimmedKey] : `{{${trimmedKey}}}`;
  });
};

/**
 * Interface for WhatsApp message generation parameters
 */
export interface GenerateWhatsAppMessageParams {
  categoryName?: string;
  companyName?: string;
  location?: string;
  recruiterFirstName?: string;
  recruiterPhone?: string;
}

/**
 * Interface for WhatsApp message result
 */
export interface WhatsAppMessageResult {
  plainMessage: string;
  encodedMessage: string;
}

export const generateStandaloneWhatsAppMessage = (
  params: GenerateWhatsAppMessageParams
): WhatsAppMessageResult => {
  const {
    categoryName = "",
    companyName = "",
    location = "",
    recruiterFirstName = "",
    recruiterPhone = ""
  } = params;

  // Get the default template (first one in the array)
  const defaultTemplate = waBroadCastMessageTemplates[0];

  // Prepare template variables
  const templateVariables = {
    job_category: categoryName,
    company_name: companyName,
    address: location,
    recruiter_first_name: recruiterFirstName,
    recruiter_phone: recruiterPhone
  };

  // Replace placeholders with actual values
  const plainMessage = replacePlaceholders(
    defaultTemplate.template,
    templateVariables
  );

  // Encode message for WhatsApp URL
  const encodedMessage = encodeURIComponent(plainMessage);

  return {
    plainMessage,
    encodedMessage
  };
};

/**
 * Checks if free unlock is available (has free unlocks but no unlock permission)
 * Used when bulkType is already known to be "unlock" or in unlock-specific contexts
 * @param freeCountLeft - Number of free unlocks remaining
 * @param hasUnlockPermission - Whether user has unlock permission
 * @returns true if free unlock is available
 */
export const hasFreeUnlockWithoutPermission = (
  freeCountLeft: number,
  hasUnlockPermission: boolean
): boolean => {
  return freeCountLeft > 0 && !hasUnlockPermission;
};

/**
 * Checks if unlock is restricted (no free unlocks left and no unlock permission)
 * @param freeCountLeft - Number of free unlocks remaining
 * @param hasUnlockPermission - Whether user has unlock permission
 * @returns true if unlock is restricted
 */
export const isUnlockRestricted = (
  freeCountLeft: number,
  hasUnlockPermission: boolean
): boolean => {
  return freeCountLeft === 0 && !hasUnlockPermission;
};

/**
 * Checks if free unlock is available for bulk unlock operations
 * @param bulkType - Type of bulk operation ("unlock", "download", etc.)
 * @param freeCountLeft - Number of free unlocks remaining
 * @param hasUnlockPermission - Whether user has unlock permission
 * @returns true if free unlock is available
 */
export const hasFreeUnlockAvailable = (
  bulkType: string | null,
  freeCountLeft: number,
  hasUnlockPermission: boolean
): boolean => {
  return bulkType === "unlock" && freeCountLeft > 0 && !hasUnlockPermission;
};

/**
 * Checks if the free unlock limit is reached
 * @param bulkType - Type of bulk operation ("unlock", "download", etc.)
 * @param freeCountLeft - Number of free unlocks remaining
 * @param hasUnlockPermission - Whether user has unlock permission
 * @param selectedCount - Number of currently selected candidates
 * @returns true if free unlock limit is reached
 */
export const isFreeUnlockLimitReached = (
  bulkType: string | null,
  freeCountLeft: number,
  hasUnlockPermission: boolean,
  selectedCount: number
): boolean => {
  return (
    hasFreeUnlockAvailable(bulkType, freeCountLeft, hasUnlockPermission) &&
    selectedCount >= freeCountLeft
  );
};

/**
 * Gets the message text for free unlock selection limit in BulkActionBanner
 * @param freeCountLeft - Number of free unlocks remaining
 * @returns Message string
 */
export const getFreeUnlockSelectionMessage = (
  freeCountLeft: number
): string => {
  return `Select max ${freeCountLeft} free unlock allowed by admin`;
};

/**
 * Gets the message text for selected candidates with free unlock limit in bulk-unlock pages
 * @param selectedCount - Number of selected candidates
 * @param freeCountLeft - Number of free unlocks remaining
 * @returns Message string
 */
export const getFreeUnlockSelectedMessage = (
  selectedCount: number,
  freeCountLeft: number
): string => {
  return `Selected ${selectedCount} of max. ${freeCountLeft} free unlock allowed by admin`;
};
